import { useModal, DateField } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { RenewalService, SubmissionService } from 'gw-capability-gateway';
import { ENTGatewayDocumentService } from 'ent-capability-gateway-document';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useRef, useEffect } from 'react';
import { TranslatorContext } from '@jutro/locale';
import messages from './DocumentUploadComponent.messages.js';
import metadata from './DocumentUploadComponent.metadata.json5';
import styles from '../TWIAAdditionalInterestComponent.module.scss';

import { Button, InputField, DropdownSelectField } from '@jutro/legacy/components';

import { Link } from '@jutro/router';

function DocumentUploadComponent(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { showAlert, showConfirm } = useModal();
    const { submissionVM, skipReviewActivity, updateWizardData, showErrors, findInvalidComponent, onValidate } = props;
    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();
    const [documentSearch, setDocumentSearch] = useState('');
    const [documentTypes, setDocumentTypes] = useState([]);
    const [documentType, setDocumentType] = useState();
    const [documents, setDocuments] = useState([]);
    const [displayDocuments, setDisplayDocuments] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [loading, setLoading] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useContext(TranslatorContext);
    const documentTypeCodes = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.DocumentType').codes;
    // DE16847 add NA option to building list
    const buildingOptionNA = {
        code: 0,
        name: 'N/A'
    };
    const formattedDocumentTypeCodes =  documentTypeCodes?.map(code => ({
        code: code.code,
        name: translator({ id: code.name })
    }));
    let allowedDocumentTypes = _.get(submissionVM, 'lobData.commercialProperty.allowedDocumentTypes_TWIA');
    allowedDocumentTypes = _.get(allowedDocumentTypes, 'aspects.availableValues');
    const locations = _.get(submissionVM, 'lobData.commercialProperty.coverables.locations.value');

    const loadDocuments = () => {
        setLoading(true);
        // US26438
        const isRenewal = _.get(submissionVM, 'baseData.jobType.value.code') === 'Renewal';
        const jobNumber = _.get(submissionVM, 'quoteID.value') || _.get(submissionVM, 'jobID.value');
        const getDocumentService = isRenewal? RenewalService.getDocumentsForRenewal(jobNumber, authHeader) : 
        SubmissionService.getDocumentsForSubmission(jobNumber, authHeader)
        
        getDocumentService.then(docs => {
            setDocuments(docs);
            setDisplayDocuments(docs);
        })
        .catch(e => {
            showAlert({
                title: commonMessages.errorUploadTitle,
                message: messages.unableToDownload,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            }).catch(_.noop);
        }) 
        .finally(() => {
            setLoading(false);
        }); 
    };

    useEffect(() => {
        const mappedDocTypeValues = allowedDocumentTypes && allowedDocumentTypes.map(docType => {
             // DE16868 add other document type description
             const name = docType.code === 'other' ? messages.otherDocument : { id: docType.name, defaultMessage: docType.name };
             return {
                 code: docType.code,
                 name: translator(name)
             };
        });
        setDocumentTypes(mappedDocTypeValues);
        const locationMap = locations && new Map(locations.map(l => [l.publicID, l]));
        let mappedBuildings = locations && locations.flatMap(l => l.buildings);
        mappedBuildings = mappedBuildings && mappedBuildings.map(building => {
             // DE16862 fix building description
             const loc = locationMap && locationMap.get(building.locationId);
             const desc = _.isNil(building.description) ? '' : building.description;
             const name = `${building.buildingNumber}: ${desc} (${loc.displayName})`;
             return {
                 code: building.fixedID,
                 name: name
             };
        });
        // DE16847 add NA option to building list
        mappedBuildings.unshift(buildingOptionNA);
        setBuildings(mappedBuildings);
        setDocumentSearch('');
        loadDocuments();
    }, []);

    const onUploadDocument = useCallback(item => {
        const documentMetaDataTemplate = {
            docUID: '001',
            documentType: documentType,
            securityType: 'unrestricted',
            status: 'approved',
            jobNumber: _.get(submissionVM, 'quoteID.value') || _.get(submissionVM, 'jobID.value'), // US26438
            name: item.name,
            // 2025-05-22 - Debra Frum - DE19212 - TWIA Underwriting Response activities are triggering under open transactions
            //   Functionality for TWIA didn't get implemented when TFPA & TWIA portals merged during move to Guidewire Cloud
            // 2022-05-24 - Debra Frum - US22167 - PC / PE Underwriting Response Activity
            //   When true, a Review activity won't be generated when a document is uploaded
            skipReviewActivity_TWIA: skipReviewActivity,
            sessionID: '-'
        };
        setLoading(true);
        ENTGatewayDocumentService.uploadDocument(
            item,
            documentMetaDataTemplate,
            authHeader
        )
        .then(loadDocuments)
        .catch(e => {
            // US26278 popup for bad file type
            if (_.get(e, 'appErrorCode') === 610) {
                showAlert({
                    title: messages.badFileTypeTitle,
                    message: messages.badFileTypeBody,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            } else {
                showAlert({
                    title: commonMessages.errorUploadTitle,
                    message: commonMessages.uploadFailedMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            }
            setLoading(false);
        })
    }, [ENTGatewayDocumentService, submissionVM, showAlert, authHeader, documentType]);

    const onChangeDocumentType = useCallback(
        docType => {
            setDocumentType(docType);
        },
        [setDocumentType]
    );

    const onChangeDocumentSearch = useCallback(
        searchTerm => {
            if (searchTerm && documents && documents.length > 0) {
                 const filtered = documents?.filter(doc => doc.name?.includes(searchTerm));
                 setDisplayDocuments(filtered);
            } else {
                 setDisplayDocuments(documents);
            }
            setDocumentSearch(searchTerm);
        },
        [setDocumentSearch, setDisplayDocuments, documents]
    );

    const onDocumentUpdate = useCallback(item => {
        setLoading(true);
        ENTGatewayDocumentService.updateDocument(
            item,
            authHeader
        )
        .then(result => {
            item.buildingDisplay_TWIA = _.get(result, 'buildingDisplay_TWIA');
            item.edited = false;
        })
        .catch(() => {
            showAlert({
                title: commonMessages.errorUploadTitle,
                message: commonMessages.uploadFailedMessage,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            }).catch(_.noop);
        })
        .finally(() => {
            setLoading(false);
        }); 
    }, [ENTGatewayDocumentService, submissionVM, showAlert, authHeader, documentType]);

    const isEditable = (item) => {
      return _.isNil(item.description) || item.edited;
    };

    const onDownloadDocument = useCallback(async item => {
        setLoading(true);
        if (skipReviewActivity) {
            _.set(item, 'skipReviewActivity_TWIA', skipReviewActivity);
        }
        try {
            const sessionID = await ENTGatewayDocumentService.generateDownloadToken(item, authHeader);
            const downloadURL = ENTGatewayDocumentService.downloadDocument(item.docUID, sessionID);
            const jobNumber = _.get(submissionVM, 'quoteID.value');
            window.location.href = `${downloadURL}&job=${jobNumber}`;
        } catch (error) {
             showAlert({
                 title: commonMessages.errorUploadTitle,
                 message: commonMessages.errorGenerateUploadToken,
                 status: 'error',
                 icon: 'mi-error-outline',
                 confirmButtonText: commonMessages.ok,
             })
             .catch(_.noop);
        } finally {
             setLoading(false);
        }
    }, [ENTGatewayDocumentService, showAlert]);

    const getDocumentName = useCallback((item, index) => (
        isEditable(item) ? item.name : <Link style={{ justifyItems: 'left' }} onClick={() => { onDownloadDocument(item) }}>{item.name}</Link>
    ), [onDownloadDocument]);

    const getDocumentType = (item) => {
        const code = formattedDocumentTypeCodes?.find(val => val.code == item.documentType);
        return code ? code.name : item.documentType;
    };

    const writeDocumentValue = useCallback((index, path, value) => {
        const doc = _.cloneDeep(displayDocuments[index]);
        _.set(doc, path, value);
        doc.edited = true;
        const updatedDocuments = _.cloneDeep(displayDocuments);
        updatedDocuments[index] = doc;
        setDocuments(updatedDocuments);
        setDisplayDocuments(updatedDocuments);
        refreshData();
    }, [displayDocuments, setDocuments, setDisplayDocuments, refreshData]);

    const getDocumentDescription = useCallback((item, index) => {
        return (
            isEditable(item) ?
            <InputField
                id={`description${index}`}
                path="description"
                onValueChange={(value) => {writeDocumentValue(index, 'description', value)}}
                value={item.description}
                required
                showRequired
            /> : item.description
        )
    }, [writeDocumentValue]);

    const getDocumentBuilding = useCallback((item, index) => {
        return (
            isEditable(item) ?
            <DropdownSelectField
                id={`building${index}`}
                alwaysShowPlaceholder={false}
                availableValues={buildings}
                path="buildingFixedID_TWIA"
                onValueChange={(value) => {writeDocumentValue(index, 'buildingFixedID_TWIA', value)}}
                value={item.buildingFixedID_TWIA}
            /> : item.buildingDisplay_TWIA
        )
    }, [writeDocumentValue]);

    const getDocumentUploaded = useCallback((item, index) => (
        <DateField
            id={`uploadedDate${index}`}
            value={item.dateModified}
            readOnly
            displayFormat="vshort"
        />
    ), []);

    const getDocumentUpdated = useCallback((item, index) => (
        isEditable(item) ?
        <Button
            id={`update${index}`}
            onClick={() => onDocumentUpdate(item)}
            size="small"
            icon="mi-save"
        /> : <div></div>
    ), []);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        documentUploadContainer: {
            visible: !loading
        },
        loadingIndicator: {
            visible: loading
        },
        uploadDocumentType: {
            availableValues: documentTypes,
            value: documentType
        },
        uploadDocument: {
            disabled: documentType === undefined
        },
        documentSearch: {
            visible: documents && documents.length > 0,
            value: documentSearch
        },
        documentsTable: {
            data: displayDocuments
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {},
        resolveCallbackMap: {
            onUploadDocument,
            onChangeDocumentType,
            onChangeDocumentSearch,
            getDocumentName,
            getDocumentDescription,
            getDocumentType,
            getDocumentBuilding,
            getDocumentUploaded,
            getDocumentUpdated
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={submissionVM}
            overrideProps={overrideProps}
            onModelChange={updateWizardData}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

export default DocumentUploadComponent;
